import { clearSession, getAccessToken } from './session';

const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:3001';
};

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiListResponse<T> = { data: T[] };

export type TripRecord = {
  id: string;
  employee_id: string;
  destination?: string;
  purpose?: string;
  start_date?: string;
  end_date?: string;
  created_at?: string;
  status?: string;
  estimated_budget?: number;
};

export type CreateTripPayload = {
  destination: string;
  purpose: string;
  start_date: string;
  end_date: string;
  estimated_budget: number;
};

export type UpdateTripPayload = Partial<CreateTripPayload>;

export type AdminRecord = {
  id: string;
  company_id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export type EmployeeDirectoryRecord = {
  id: string;
  email: string;
  name: string;
  role: string;
  department?: string | null;
  designation?: string | null;
  manager_id?: string | null;
  manager_name?: string | null;
  manager_email?: string | null;
};

export type TripBudgetRevisionRecord = {
  id: string;
  trip_id: string;
  company_id: string;
  changed_by: string;
  old_budget: number;
  new_budget: number;
  reason?: string | null;
  created_at?: string;
};

const buildUrl = (path: string, query?: Record<string, string | number | undefined>): string => {
  const url = new URL(`${getApiBaseUrl()}${path}`);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
};

const request = async <T>(path: string, options?: RequestInit, query?: Record<string, string | number | undefined>): Promise<T> => {
  const token = getAccessToken();
  if (!token) {
    throw new ApiError('You are not logged in. Please sign in with Google first.', 401);
  }

  const response = await fetch(buildUrl(path, query), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers || {}),
    },
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      clearSession();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.replace('/login');
      }
    }

    const message =
      payload && typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed with status ${response.status}`;
    throw new ApiError(message, response.status);
  }

  return payload as T;
};

export const getTrips = async (params?: { status?: string; page?: number; perPage?: number }): Promise<TripRecord[]> => {
  const response = await request<ApiListResponse<TripRecord>>(
    '/api/trips',
    { method: 'GET' },
    {
      status: params?.status,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 500,
    }
  );
  return response.data || [];
};

export const getTripById = async (id: string): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(`/api/trips/${id}`, { method: 'GET' });
  return response.data;
};

export const createTrip = async (payload: CreateTripPayload): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(
    '/api/trips',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.data;
};

export const updateTrip = async (id: string, payload: UpdateTripPayload): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(
    `/api/trips/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
  return response.data;
};

export const deleteTrip = async (id: string): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(
    `/api/trips/${id}`,
    { method: 'DELETE' },
  );
  return response.data;
};

export const submitTrip = async (id: string): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(
    `/api/trips/${id}/submit`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return response.data;
};

export const approveTrip = async (id: string, action: 'approve' | 'reject', comment?: string): Promise<TripRecord> => {
  const response = await request<{ data: TripRecord }>(
    `/api/trips/${id}/approve`,
    {
      method: 'POST',
      body: JSON.stringify({
        action,
        comment: comment || '',
      }),
    },
  );
  return response.data;
};

export const getAdmins = async (): Promise<AdminRecord[]> => {
  const response = await request<ApiListResponse<AdminRecord>>('/api/admins', { method: 'GET' });
  return response.data || [];
};

export const getCompanyEmployees = async (): Promise<EmployeeDirectoryRecord[]> => {
  const response = await request<ApiListResponse<EmployeeDirectoryRecord>>('/api/admins/employees', { method: 'GET' });
  return response.data || [];
};

export const getTripBudgetRevisions = async (params?: { tripId?: string; page?: number; perPage?: number }): Promise<TripBudgetRevisionRecord[]> => {
  const response = await request<ApiListResponse<TripBudgetRevisionRecord>>(
    '/api/trips/budget-revisions',
    { method: 'GET' },
    {
      trip_id: params?.tripId,
      page: params?.page ?? 1,
      per_page: params?.perPage ?? 200,
    }
  );
  return response.data || [];
};

export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
};
